import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function RetailPriceManager() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState({ purchases: [], prices: [] });
  const [products, setProducts] = useState([]);
  const [pending, setPending] = useState([]);

  const load = async () => {
    const [m, p, pp] = await Promise.all([
      api.get(`/retail/${clientId}/prices/manage`).then(r => r.data),
      api.get("/products").then(r => r.data),
      isAdminUser(user) ? api.get("/retail/admin/pending").then(r => r.data) : Promise.resolve([]),
    ]);
    setData(m); setProducts(p); setPending(pp);
  };
  useEffect(() => { load(); }, [clientId]);

  const submitPrice = async (productId, price) => {
    try {
      await api.post(`/retail/${clientId}/prices`, { product_id: productId, retail_price: Number(price) });
      toast.success("Submitted for approval"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const approve = async (cid, pid) => {
    await api.post(`/retail/${cid}/prices/${pid}/approve`);
    toast.success("Approved"); load();
  };

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header>
        <p className="label-eyebrow">Resale catalog</p>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Retail prices</h1>
        <p className="text-gray-500 mt-1">Set per-product retail prices for resale. Admin approval required before publishing.</p>
      </header>

      <Card className="p-5">
        <p className="label-eyebrow mb-3">Eligible products (purchased wholesale)</p>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 text-left"><tr><th className="py-2">Product</th><th>Wholesale</th><th>Qty bought</th><th>Retail price</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {data.purchases.map(pp => {
              const prod = products.find(x => x.id === pp.product_id);
              const existing = data.prices.find(x => x.product_id === pp.product_id);
              if (!prod) return null;
              return (
                <tr key={pp.product_id} className="border-t border-[var(--border)]" data-testid={`price-row-${pp.product_id}`}>
                  <td className="py-2 font-medium">{prod.name}</td>
                  <td>${prod.wholesale_price.toFixed(2)}</td>
                  <td>{pp.qty_purchased}</td>
                  <td>
                    <PriceInput defaultValue={existing?.retail_price || ""} onSave={(v) => submitPrice(pp.product_id, v)} testid={`price-input-${pp.product_id}`} />
                  </td>
                  <td>{existing ? (existing.approved ? <span className="text-emerald-600 font-semibold">Approved</span> : <span className="text-amber-600 font-semibold">Pending</span>) : <span className="text-gray-400">Not set</span>}</td>
                  <td></td>
                </tr>
              );
            })}
            {data.purchases.length === 0 && <tr><td colSpan={6} className="text-center text-gray-400 py-6">No eligible products yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      {isAdminUser(user) && (
        <Card className="p-5" data-testid="admin-pending-prices">
          <p className="label-eyebrow mb-3">Pending approvals (all clients)</p>
          {pending.length === 0 ? <p className="text-sm text-gray-400">Nothing pending.</p> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500 text-left"><tr><th>Client</th><th>Product</th><th>Wholesale</th><th>Retail</th><th></th></tr></thead>
              <tbody>
                {pending.map(p => (
                  <tr key={`${p.client_id}-${p.product_id}`} className="border-t border-[var(--border)]">
                    <td className="py-2">{p.client_name}</td>
                    <td>{p.product_name}</td>
                    <td>${p.wholesale_price.toFixed(2)}</td>
                    <td>${p.retail_price.toFixed(2)}</td>
                    <td><Button size="sm" className="btn-brand" onClick={() => approve(p.client_id, p.product_id)} data-testid={`approve-${p.client_id}-${p.product_id}`}>Approve</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}

function PriceInput({ defaultValue, onSave, testid }) {
  const [v, setV] = useState(defaultValue);
  return (
    <div className="flex gap-2">
      <Input type="number" step="0.01" value={v} onChange={(e) => setV(e.target.value)} className="w-24" data-testid={testid} />
      <Button size="sm" variant="outline" onClick={() => onSave(v)}>Save</Button>
    </div>
  );
}
